import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Tab, Tabs } from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'store';

import { CustomTabPanel, a11yProps } from '../settings/tabPanelUtils';
import { KitHeader } from '../shared/designKit';
import RulesTab from './RulesTab';
import UpcomingTab from './UpcomingTab';
import SentTab from './SentTab';

// Main-menu page (Asiakkaat → Huoltomuistutukset), admin + foreman. Follows the
// Asiakaspalaute pattern: operational lists live here, Settings → Ominaisuudet
// holds the org's own feature toggle. Route is feature-gated in App.js.
export default function ServiceRemindersPage() {
  const { t } = useTranslation();
  const orgId = useUserStore((s) => s.currentOrganization?.organization_id);

  const [tabValue, setTabValue] = useState(0);
  // Trigger catalog (labels, defaults, sms frame + price) shared by all tabs
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(false);

  const fetchCatalog = useCallback(async () => {
    try {
      const response = await axios.get('/api/reminder-rules/triggers/', {
        params: { organization: orgId },
      });
      setCatalog(response.data);
    } catch (err) {
      console.error('Failed to load reminder trigger catalog:', err);
      setCatalogError(true);
    }
  }, [orgId]);

  useEffect(() => {
    if (orgId) {
      fetchCatalog();
    }
  }, [orgId, fetchCatalog]);

  const tabs = [
    { key: 'rules', label: t('service_reminders.tab_rules') },
    { key: 'upcoming', label: t('service_reminders.tab_upcoming') },
    { key: 'sent', label: t('service_reminders.tab_sent') },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }} data-testid="service-reminders-page">
      <KitHeader
        icon={<NotificationsActiveOutlinedIcon />}
        title={t('service_reminders.tab_label')}
        subtitle={t('service_reminders.intro')}
        sx={{ mb: 2.5 }}
      />

      {catalogError && <Alert severity="error">{t('service_reminders.load_error')}</Alert>}

      {!catalogError && !catalog && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {catalog && (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={(e, v) => setTabValue(v)}
              aria-label="service reminder tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 14, minHeight: 44 }, minHeight: 44 }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={tab.key}
                  label={tab.label}
                  {...a11yProps(index, 'service_reminders')}
                  data-testid={`reminders-tab-${tab.key}`}
                />
              ))}
            </Tabs>
          </Box>

          <CustomTabPanel value={tabValue} index={0} idPrefix="service_reminders">
            <RulesTab catalog={catalog} />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={1} idPrefix="service_reminders">
            <UpcomingTab />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={2} idPrefix="service_reminders">
            <SentTab catalog={catalog} />
          </CustomTabPanel>
        </>
      )}
    </Box>
  );
}
